import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Stack, useMediaQuery, Button } from "@mui/material";
import BackgroundImage from '../assets/plus_top_picture.png';
import { useTranslation } from 'react-i18next';

import { Search } from 'components/Search';
import { paths } from 'routes';
import { useLocalizedNavigate } from 'website/components/LocalizedLink';

export const TopSection = () => {
  const { t } = useTranslation();
  const boxRef = useRef(null);
  const [boxInnerHeight, setBoxInnerHeight] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useLocalizedNavigate();

  useEffect(() => {
    function updateHeight() {
      if (boxRef.current) {
        setBoxInnerHeight(boxRef.current.offsetHeight);
      }
    }

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const isXs = useMediaQuery('(max-width: 899px)');
  const isSmallMd = useMediaQuery('(min-width: 900px) and (max-height: 700px)');

  const hStyles = {
    fontSize: isXs ? '1.75rem' : (isSmallMd ? '2.875rem' : '3rem'),
    fontWeight: 600,
    lineHeight: "normal",
    textTransform: "capitalize",
    fontFeatureSettings: `'liga' off, 'clig' off`,
    color: "grey.900",
    alignSelf: "stretch",
    textAlign: isXs ? "center" : "left",
  };

  const boxHeight = isXs || isSmallMd ? "auto" : 398;

  const handleSearchSubmit = (value) => {
    navigate(`${paths.website.jobAds}?search=${encodeURIComponent(value || searchTerm)}`);
  };

  return (
    <Box
      display={"flex"}
      sx={{
        position: "relative",
        width: "100vw",
        height: [
          "calc(100dvh - 70px)",
          "calc(100dvh - 70px - constant(safe-area-inset-bottom))",
          "calc(100dvh - 70px - env(safe-area-inset-bottom, 0px))"
        ],
        marginLeft: { md: "-50vw" },
        left: { md: "50%" },
        backgroundImage: `url(${BackgroundImage})`,
        backgroundPosition: 'center',
        backgroundSize: "cover",
        backgroundRepeat: 'no-repeat',
      }}
    >
        <Box
          display={"flex"}
          ref={boxRef}
          sx={{
            maxWidth: {xs: "392px", md: "650px"},
            p: {xs: 3, md:"50px"},
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 3.25,
            borderRadius: "0px 30px 30px 0",
            background: "rgba(237, 243, 253, 0.8)",
            position: "absolute",
            top: {xs: '50%', md: `calc((100vh - 70px - env(safe-area-inset-bottom) - ${boxInnerHeight}px) / 2)`},
            left: 0,
            transform: {xs: 'translateY(-50%)', md: 'none'},
          }}
        >
          <Typography variant="h2" sx={hStyles}>
            {t('website:homepage.topSection.heading')}
          </Typography>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={2}
            width="100%"
            alignItems="center"
          >
            <Search
              fullWidth={isXs ? false : true}
              placeholder={t('website:homepage.topSection.searchPlaceholder')}
              buttonLabel={t('website:homepage.topSection.searchButton')}
              initialValue={searchTerm}
              handleSearchChange={(e) => setSearchTerm(e.target.value)}
              onSearchButtonClick={isXs ? null : handleSearchSubmit}
              sx={{
                minWidth: 200,
                height: 40,
                py: 2.5,
                px: 3.5,
                borderRadius: 1,
                alignSelf: "stretch",
                border: "1px solid",
                borderColor:"#13373F33",
                backgroundColor: "background.paper",
                boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.08)",
              }}
              inputSx={{
                border: "none",
                '& .MuiOutlinedInput-input': {
                  fontSize: "1rem",
                  fontWeight: 400,
                  fontFeatureSettings: `'liga' off, 'clig' off`,
                  color: 'text.black',
                  lineHeight: '24px',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: "text.placeholder",
                  fontFeatureSettings: '"liga" off, "cling" off',
                  fontSize: '1rem',
                  fontWeight: 400,
                  lineHeight: '150%',
                },
                pl: 0,
                pr: 0,
              }}
              iconSx={{
                svg: {
                  height: 24,
                  color: 'primary.light',
                  strokeWidth: 0,
                }
              }}
            />
            {isXs && (
                <Button
                    size="medium"
                    variant="contained"
                    onClick={() => handleSearchSubmit(searchTerm)}
                    sx={{
                        width: 1,
                        height: 40,
                        backgroundColor: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'primary.main',
                        },
                    }}
                >
                    {t('website:homepage.topSection.searchButton')}
                </Button>
            )}
          </Stack>
        </Box>
    </Box>
  )
}